import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import type { SentimentType } from "@repo/ui/Buttons/sentimentToggleButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { ModalCloseButton } from "@repo/ui/Modal/modalCloseButton";
import { IconCircleCheck, IconExternalLink } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { Dialog, Header } from "react-aria-components";

export interface VoiceAgentCallFeedbackPopoverContentProps {
  sentimentType: SentimentType;
  onSubmit: (commentary: string) => Promise<void>;
  onEditAgent?: (sentiment: SentimentType, commentary: string) => void;
  existingFeedback: {
    sentiment: "positive" | "negative";
    commentary: string;
  } | null;
}

export const VoiceAgentCallFeedbackPopoverContent: FC<
  VoiceAgentCallFeedbackPopoverContentProps
> = (props) => {
  // Initialize state directly from props - component will remount when popover opens/closes
  const initialCommentary =
    props.existingFeedback?.sentiment === props.sentimentType
      ? props.existingFeedback.commentary
      : "";

  const [commentary, setCommentary] = useState<string>(initialCommentary);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await props.onSubmit(commentary.trim());
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const promptText =
    props.sentimentType === "positive"
      ? "What did the agent do well on this call?"
      : "What should the agent have done differently?";

  return (
    <Dialog className="space-y-2 overflow-auto p-4">
      {({ close }) => (
        <>
          {isSubmitted ? (
            // Success state after submission
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <IconCircleCheck className="size-5 text-success" />
                <span className="text-sm font-medium">Feedback saved</span>
              </div>

              {props.onEditAgent ? (
                <>
                  <p className="text-sm text-gray-600">
                    Want to adjust your agent based on this call? We can suggest
                    improvements to your configuration.
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant={ButtonVariant.Secondary}
                      size={Size.MEDIUM}
                      onClick={close}
                    >
                      Done
                    </Button>
                    <Button
                      variant={ButtonVariant.Primary}
                      size={Size.MEDIUM}
                      onClick={() => {
                        close();
                        props.onEditAgent?.(props.sentimentType, commentary);
                      }}
                      icon={<IconExternalLink className="size-4" />}
                    >
                      Review suggestions
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex justify-end">
                  <Button
                    variant={ButtonVariant.Secondary}
                    size={Size.MEDIUM}
                    onClick={close}
                  >
                    Done
                  </Button>
                </div>
              )}
            </div>
          ) : (
            // Form state
            <>
              <div className="flex items-center justify-between">
                <Header
                  slot="title"
                  className="text-lg font-semibold text-foreground"
                >
                  Share agent feedback
                </Header>
                <ModalCloseButton onPress={close} />
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {promptText}
                  </p>

                  <Textarea
                    value={commentary}
                    onChange={setCommentary}
                    placeholder="Share your feedback..."
                    height="5rem"
                  />

                  <p className="text-xs text-muted-foreground">
                    After you submit feedback, Alpharun can generate suggestions
                    to improve your agent configuration.
                  </p>
                </div>

                <div className="flex justify-end space-x-2">
                  <Button
                    variant={ButtonVariant.Secondary}
                    size={Size.MEDIUM}
                    onClick={close}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant={ButtonVariant.Primary}
                    size={Size.MEDIUM}
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    isDisabled={
                      commentary.trim().length === 0 ||
                      commentary.trim() === initialCommentary.trim()
                    }
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </Dialog>
  );
};
